import {
  DEFAULT_DATE_BASIS,
  type DateBasis,
} from "@repo/app-types/entities/dateBasis";
import {
  defaultDateRange,
  type DateRange,
} from "@repo/app-types/entities/dateRange";
import type {
  CoachingGoalCondition,
  CustomFieldCondition,
  OutcomeCondition,
  PlaybookCondition,
  SignalMetadataCondition,
  TotalRecordingDurationCondition,
} from "@repo/app-types/entities/filterConditions";
import type { GroupId } from "@repo/app-types/entities/group";
import type { SignalId } from "@repo/app-types/entities/signal";

export type FilterKey = (typeof allFilterKeys)[number];

/* prettier-ignore */
const allFilterKeys = [
  "teammateGroupIds",
  "byGroupTeammateGroupIds",
  "dateRange",
  "dateBasis",
  "playbookConditions",
  "customFields",
  "outcome",
  "signalId",
  "signalMetadataFields",
  "totalRecordingDuration",
  "coachingGoal"
] as const;

export type FilterValues = Record<FilterKey, unknown> & {
  // `undefined` means "not yet loaded from teammate preferences"; `null`
  // means "All teammates" (no group filter applied); an array is the
  // multi-select set of group ids.
  teammateGroupIds: readonly GroupId[] | null | undefined;
  // Independent teammate-group filter scoped to the byGroup rollup page.
  // No teammate preference is associated, so there's no async-load state:
  // `null` means "All teammate groups" (no filter); an array is the
  // multi-select set of group ids.
  byGroupTeammateGroupIds: readonly GroupId[] | null;
  dateRange: DateRange;
  /** Which date `dateRange` applies to. */
  dateBasis: DateBasis;
  playbookConditions: readonly PlaybookCondition[];
  customFields: readonly CustomFieldCondition[];
  outcome: OutcomeCondition | null;
  signalId: SignalId | null;
  signalMetadataFields: readonly SignalMetadataCondition[];
  totalRecordingDuration: TotalRecordingDurationCondition | null;
  coachingGoal: CoachingGoalCondition | null;
};

export const defaultFilterValues: FilterValues = {
  teammateGroupIds: undefined,
  byGroupTeammateGroupIds: null,
  dateRange: defaultDateRange,
  dateBasis: DEFAULT_DATE_BASIS,
  playbookConditions: [],
  customFields: [],
  // null (not undefined) because these have no async default-loading behavior,
  // unlike teammateGroupIds which uses undefined to indicate "not yet loaded from preferences"
  outcome: null,
  signalId: null,
  signalMetadataFields: [],
  totalRecordingDuration: null,
  coachingGoal: null,
};
